import { useEffect, useState } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { throwValidationError, useDataService } from '../../../services/ApiDataService';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { createAlertConfig, ERROR } from '../../../stores/AlertsStore';
import { getLogger } from '../../../utilities/UseLog';
import { buildRootPageTarget } from '../AdminLinkUtils';
import { PLM_ID_FIELD } from './PlmConstants';

const log = getLogger('v3ApiHelper');

function executePost(dataService, url, cfg) {
    const { beforeSubmit, afterSubmit, errorHandler, setFinding } = cfg;
    const queryStart = () => {
        setFinding(true);
    };

    const queryEnd = () => {
        setFinding(false);
    };

    return (data) =>
        Promise.resolve(queryStart())
            .then(() => beforeSubmit(data))
            .then((body) => {
                return dataService.post(url, body);
            })
            .then((response) => afterSubmit(response))
            .then((response) => getData(response))
            .catch(errorHandler)
            .finally(queryEnd);
}

export function useDeleteOne(callerConfig) {
    const [, setFinding] = useState(false);
    const cfg = _.defaults(callerConfig, {
        beforeSubmit: (formData) => formData,
        afterSubmit: (response) => response,
        errorHandler: useErrorWrapper(),
        setFinding,
    });
    const { url, recordId } = cfg;

    const dataService = useDataService();
    return executePost(dataService, `${url}/${recordId}/delete`, cfg);
}

export function useUpdateOne(callerConfig) {
    const [, setFinding] = useState(false);
    const cfg = _.defaults(callerConfig, {
        beforeSubmit: (formData) => formData,
        afterSubmit: (response) => response,
        errorHandler: useErrorWrapper(),
        setFinding,
    });
    const { url, recordId } = cfg;
    const dataService = useDataService();
    if (_.isEmpty(recordId)) {
        return executePost(dataService, `${url}/create`, cfg);
    }
    return executePost(dataService, `${url}/${recordId}/modify`, cfg);
}

function getData(response) {
    const data = _.get(response, 'data', {});
    data._id = data.id;
    return data;
}

export function useFirstAlert(errorObject) {
    const errors = errorObject?.responseBody?.errors;
    if (!_.isEmpty(errors)) {
        return createAlertConfig(ERROR, i18n._(t`${errors[0]?.message}`));
    }
    return null;
}

export function useSetErrors(setError) {
    return (errorObject) => {
        const errors = errorObject?.responseBody?.errors;
        if (!_.isEmpty(errors)) {
            setError(errors[0].property, {
                type: ERROR,
                message: t`${errors[0].message}`,
            });
            log.warn(`${JSON.stringify(errors)}`);
            return throwValidationError();
        }
        return null;
    };
}

export function useGetOne(callerConfig) {
    const dataService = useDataService();
    const [finding, setFinding] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [reloadTrigger, setReloadTrigger] = useState();
    const [formData, setFormData] = useState({});
    const { errorWrapper } = useErrorWrapper();
    const funcConfig = _.defaults(callerConfig, {
        postFindByIdHandler: (response) => response,
        defaultValues: {},
        errorHandler: errorWrapper(),
    });

    const resetForm = () => setReloadTrigger(_.uniqueId());

    const queryStart = () => {
        setFinding(true);
    };

    const queryEnd = () => {
        setFinding(false);
        setInitialized(true);
    };

    useEffect(() => {
        if (!funcConfig.recordId) {
            Promise.resolve(queryStart())
                .then(() => {
                    setFormData(funcConfig.defaultValues);
                    funcConfig.resetFunc(funcConfig.defaultValues);
                })
                .finally(queryEnd);
        } else {
            Promise.resolve(queryStart())
                .then(() => dataService.get(`${funcConfig.url}/${funcConfig.recordId}/details`))
                .then((response) => {
                    const data = getData(response);
                    setFormData(data);
                    funcConfig.resetFunc(data);
                })
                .catch(funcConfig.errorHandler)
                .finally(queryEnd);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger]);

    return { initialized, finding, formData, resetForm };
}

export function buildPageTarget(adminPage) {
    return `${buildRootPageTarget(adminPage)}/:${PLM_ID_FIELD}`;
}

export function useV3Api() {
    return { useDeleteOne, useUpdateOne, useGetOne, useFirstAlert, useSetErrors };
}
